import Box from "@mui/material/Box";
import { SelectChangeEvent } from "@mui/material";
import Divider from "@mui/material/Divider";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import plusIconWhite from "../../assets/icons/plus-icon-white.svg";
import { useCountryList } from "../../services/okta";
import { useFolderDropdown } from "../../services/segment/index";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import { CircularProgressCustom, PrimaryButton } from "../../utils/muiCustomTheme";
import { FormDataFilterSegment } from "./segment-interface";
import "./Segments.scss";
import SegmentsFilters from "./SegmentsFilters";
import SegmentsTableTree from "./SegmentsTableTree";
import { useFilteredAudiences } from "./useFilteredAudiences";
import { filterCancelled } from "../campaigns/filterAllHelper";
import { makeMultiSelectChangeHandler } from "../../shared/helpers/multiSelectChangeHandler";
import useFilterLocalStorage from '../../shared/hooks/useFilterLocalStorage';
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

function Segments() {
  const { headerRef, sentinelRef, isSticky, headerHeight } = useStickyHeader();
  const navigate = useNavigate();
  const { createLink } = useSimulationParams();
  const { data: countries, isLoading: isCountriesLoading } = useCountryList();

  const countryTags = Array.from(new Set(countries?.flatMap((c) => c.tags) || []));

  const countryAdminMap =
    countries?.reduce<Record<string, number>>(
      (acc: { [x: string]: any }, c: { name: string | number; admin: any }) => {
        acc[c.name] = c.admin;
        return acc;
      },
      {}
    ) || {};

  const { getFilterValue, updateFilter, filters } = useFilterLocalStorage('segments');

  const storedStatus = ('status' in filters) ? getFilterValue('status') : ['All'];
  const storedBrand = ('brand' in filters) ? getFilterValue('brand') : ['All'];
  //const storedMarket = getFilterValue('market');
  const storedCreatedBy = ('createBy' in filters) ? getFilterValue('createBy') : ['All'];
  const storedLastEditedBy = ('lastEditedBy' in filters) ? getFilterValue('lastEditedBy') : ['All'];

  const filtersParam = {
    status: storedStatus,
    brand: storedBrand,
    //market: storedMarket,
    createdBy: storedCreatedBy,
    lastEditedBy: storedLastEditedBy,
  };

  const defaultVal = {
    search: '',
    status: storedStatus,
    brand: storedBrand,
    //country: storedMarket,
    createBy: storedCreatedBy,
    lastEditedBy: storedLastEditedBy,
  };

  const methods = useForm<FormDataFilterSegment>({
    defaultValues: {
      ...defaultVal,
      refreshKey: 0,
      deletePending: false,
    }
  });

  const { watch } = methods;
  const refreshKey = watch('refreshKey');
  const deletePending = watch('deletePending');

  const { data: dataFolderDropdown, isPending: isPendingDataFolderDropdown } = useFolderDropdown({
    query: {
      tags: countryTags
    },
    options: {
      enabled: countryTags.length > 0,
      queryKey: ['segments', refreshKey],
    },
  });

  const filterDataSegmentDropdown = JSON.parse(dataFolderDropdown?.data || "[]");
  const parseDataFolderDropdown = filterCancelled(filterDataSegmentDropdown);
  const {
    inputNameValue,
    filteredData,
    statusOptions,
    brandOptions,
    createdByOptions,
    lastEditedByOptions,
  } = useFilteredAudiences(parseDataFolderDropdown, watch, filtersParam);

  useEffect(() => {
    if (statusOptions.length > 1) {
      const current = getFilterValue('status');
      if (!('status' in filters) || (current.length === 1 && current[0] === 'All')) {
        updateFilter('status', statusOptions);
      }
    }
    if (brandOptions.length > 1) {
      const current = getFilterValue('brand');
      if (!('brand' in filters) || (current.length === 1 && current[0] === 'All')) {
        updateFilter('brand', brandOptions);
      }
    }
    if (createdByOptions.length > 1) {
      const current = getFilterValue('createBy');
      if (!('createBy' in filters) || (current.length === 1 && current[0] === 'All')) {
        updateFilter('createBy', createdByOptions);
      }
    }
    if (lastEditedByOptions.length > 1) {
      const current = getFilterValue('lastEditedBy');
      if (!('lastEditedBy' in filters) || (current.length === 1 && current[0] === 'All')) {
        updateFilter('lastEditedBy', lastEditedByOptions);
      }
    }
  }, [statusOptions, brandOptions, createdByOptions, lastEditedByOptions, filters, updateFilter, getFilterValue]);

  function makeFilterChangeHandler( key: 'status' | 'brand' | 'createBy' | 'lastEditedBy', options: string[]) {
    const getPrev = () => (key in filters ? getFilterValue(key) : ['All']);
    const setVals = (vals: string[]) => updateFilter(key, vals);
    return makeMultiSelectChangeHandler(getPrev, setVals, options);
  }

  const handleStatusChange = makeFilterChangeHandler('status', statusOptions);
  const handleBrandChange = makeFilterChangeHandler('brand', brandOptions);
  const handleCreatedByChange = makeFilterChangeHandler('createBy', createdByOptions);
  const handleLastEditedChange = makeFilterChangeHandler('lastEditedBy', lastEditedByOptions);

  const navigateOnSegment = () => {
    navigate(createLink("/create/segment"));
  };

  if (isCountriesLoading || isPendingDataFolderDropdown || deletePending) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="segments">
        <div ref={sentinelRef}></div>
        <div ref={headerRef} className={`segments__wrap ${isSticky ? "is-sticky" : ""}`}>
          <div className="segments__container">
            <div className="segments__container__btn-wrap">
              <div className="segments__container__title">Segments</div>
              <div className="segments__container__btn-wrap__btn">
                <PrimaryButton variant="contained" onClick={navigateOnSegment}>
                  Create new segment
                  <img className="btn-primary__icon" src={plusIconWhite} alt="Create Segment" />
                </PrimaryButton>
              </div>
            </div>
            <Divider variant="middle" className="segments__container__divider" />
            <SegmentsFilters
              inputNameValue={inputNameValue}
              status={storedStatus.includes('All') ? statusOptions : storedStatus}
              brand={storedBrand.includes('All') ? brandOptions : storedBrand}
              createdBy={storedCreatedBy.includes('All') ? createdByOptions : storedCreatedBy}
              lastEditedBy={storedLastEditedBy.includes('All') ? lastEditedByOptions : storedLastEditedBy}
              handleStatusChange={handleStatusChange}
              handleBrandChange={handleBrandChange}
              handleCreatedByChange={handleCreatedByChange}
              handleLastEditedChange={handleLastEditedChange}
              statusOptions={statusOptions}
              brandOptions={brandOptions}
              createdByOptions={createdByOptions}
              lastEditedByOptions={lastEditedByOptions}
              clearFilters={() => {
                updateFilter('status', statusOptions);
                updateFilter('brand', brandOptions);
                updateFilter('createBy', createdByOptions);
                updateFilter('lastEditedBy', lastEditedByOptions);
              }}
            />
          </div>
        </div>
        <Box className="segments__container">
          <SegmentsTableTree data={filteredData} countryAdminMap={countryAdminMap} stickyTop={headerHeight} />
        </Box>
      </div>
    </FormProvider>
  );
}

export default Segments;
